// Typed client for the buyer Explore surface.
//
// Explore.tsx historically rendered off hardcoded literals. This module
// is the single client for Explore reads against the data engine's
// /dataengine/* proxy, plus adapters that map the wire shapes to the
// page-level types (`LaunchProperty`, `PropertyDetail`) so the renderers
// stay unchanged.
//
// Network reads are gated behind `import.meta.env.VITE_EXPLORE_API` —
// when unset the page keeps its in-file mock literals. Mirrors the
// articlesApi.ts / VITE_ARTICLES_API pattern.
//
// Scope: this is the [A1]/[A2] slice — listPipeline() + getProjectDetails().
// listHottest() / listPicked() (for [B1]/[B2]) are intentionally not here yet.

import { apiFetch, apiJson, apiPostJson, ApiError } from "./api";
import { COSTS_DEFAULT_SAMPLE_PRICE, COSTS_TXN_WINDOW_MONTHS, COSTS_MAX_DISTRICT } from "@/constants/costs";
import type { HotProperty, LaunchProperty, PickedProperty, Tone } from "@/pages/Explore";

// ─── feature flag ────────────────────────────────────────────────────────────

// Set VITE_EXPLORE_API=1 to switch Explore from mock literals to live API.
// Reading via import.meta keeps it tree-shake-safe — when unset the page
// hooks simply never invoke the network calls.
export function exploreApiEnabled(): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const env = (import.meta as any).env ?? {};
  return env.VITE_EXPLORE_API === "1" || env.VITE_EXPLORE_API === "true";
}

// ─── wire types ──────────────────────────────────────────────────────────────

// One row of GET /dataengine/ura/pipeline (uraPipeline()). No pricing
// column exists upstream — see adaptPipelineRow.
export interface PipelineRowWire {
  project: string;
  developer: string;
  street: string;
  district: string;
  market_segment: string;
  total_units: number;
  units_sold: number;
  expected_top: string;
  status: string;
  image_url?: string;
}

export interface PipelineResp {
  rows: PipelineRowWire[] | null;
  total: number;
}

// One entry of project_details.nearby_mrt (URA-canonical, structured).
export interface MrtStationWire {
  station: string;
  type: string;
  codes: string[];
  lines: string[];
  distance_m: number;
  walk_min: number;
  nearest_exit?: string;
}

// One entry of floorplan_projects.plan_types[] (assembled by the data
// engine from the floorplan_units child table). Only the fields the
// Unit-plans tab consumes are typed; everything else is ignored.
export interface PlanTypeWire {
  unit_type: string;
  classification?: string | null;
  property_type?: string | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  total_area_sqm?: number | null;
  balcony_sqm?: number | null;
  facing?: string | null;
  source_image_sha256s?: string[] | null;
}

// GET /dataengine/projects/{name}/floorplan
export interface FloorplanProjectWire {
  name: string;
  slug: string;
  plan_types?: PlanTypeWire[] | null;
}

// One row of GET /explore/picked (main-server, NOT proxied through
// /dataengine). Scored server-side against the user's workspace prefs
// — match_score 0..100, why is deterministically templated. `price` is
// the district-wide 90d median, so `price_basis` always travels with
// it; never imply a per-project ask.
export interface PickedRowWire {
  name: string;
  display_name: string;
  district?: string;
  match_score: number;
  price?: number;
  price_basis?: string;
  why: string;
  txn_count?: number;
  tenure?: string;
  image_url?: string;
  // PROP-83 — visible-review aggregate, carried through from the
  // /projects candidate row (PROP-76 attached it server-side). Always
  // present; count=0 is "no reviews yet", not field absence.
  review_avg: number;
  review_count: number;
}

export interface PickedResp {
  rows: PickedRowWire[] | null;
  // Optional empty-state hint set by the server when rows is empty:
  // "no_workspace" (anonymous / never created), "no_prefs" (no budget
  // and no areas saved), "no_candidates" (prefs set but no projects
  // matched). Drives the FE empty-state copy.
  reason?: string;
  workspace_id?: string;
  limit: number;
}

// One row of GET /dataengine/explore/hottest. Aggregated server-side
// from ura_transactions; `direction` is "up" / "down" / "flat" based
// on `psf_change_pct` (recent 45d vs prior 45d).
export interface HottestRowWire {
  name: string;
  display_name: string;
  district?: string;
  property_type?: string;
  txn_count: number;
  median_price?: number;
  median_psf?: number;
  psf_change_pct?: number;
  direction: "up" | "down" | "flat";
  image_url?: string;
  // PROP-76 — visible-review aggregate. Always present (count=0 is
  // the right answer for "no reviews yet", not field absence).
  review_avg: number;
  review_count: number;
}

export interface HottestResp {
  rows: HottestRowWire[] | null;
  limit: number;
}

// One row of GET /dataengine/ura/developer-sales (uraDeveloperSales()).
// median_price is the developer's monthly reported median transacted
// quantum for the project — the strongest per-project price signal we
// have. 0 means "reported but no median" (e.g. a launch month with no
// sales); treat it as absent, not a real S$0 figure. Only the fields
// resolveSamplePrice reads are declared — the wire carries more.
export interface DeveloperSalesRowWire {
  project: string;
  ref_period: string; // "YYYY-MM"
  lowest_price: number;
  median_price: number;
  highest_price: number;
  sold_to_date: number;
}

// One row of GET /dataengine/ura/transactions (uraTransactions()). Only
// the fields resolveSamplePrice reads are declared here.
export interface UraTxnRowWire {
  project: string;
  price: number;
  psf: number;
  area_sqm: number;
  contract_date: string; // "YYYY-MM-DD"
}

// GET /dataengine/explore/district_median (districtMedian()). median_price
// / median_psf / anchor_date are nullable — a district with no recent
// transactions returns sample_count=0 and all-null stats, which the
// resolver reads as "no signal" and degrades past.
export interface DistrictMedianWire {
  district: string;
  window_days: number;
  sample_count: number;
  anchor_date: string | null;
  median_price: number | null;
  median_psf: number | null;
}

// One row of /dataengine/projects/{name}/assets?kind=floor_plan.
export interface AssetRowWire {
  asset_sha256: string;
  kind: string;
  oss_key: string;
  url?: string;
  content_type: string;
  bytes: number;
}

export interface AssetPageWire {
  name: string;
  slug?: string | null;
  rows: AssetRowWire[];
}

// GET /dataengine/projects/{name}/details (projectDetails()). The
// listing-source profile (slug/title/description/specs) is always
// present; the URA-canonical block (tenure/district/nearby_mrt/...) is
// present only when a `projects` row exists for the name.
export interface ProjectDetailsWire {
  name: string;
  slug: string;
  category?: string;
  title?: string;
  description?: string;
  specs_parsed?: Record<string, unknown>;
  videos?: unknown[];
  ingested_at?: string;
  synced_at?: string;
  // URA-canonical fields (titlecased at the backend boundary).
  display_name?: string;
  tenure?: string;
  district?: string;
  street?: string;
  postal?: string;
  market_segment?: string;
  planning_area?: string;
  property_types?: string[];
  lat?: number;
  lng?: number;
  nearby_mrt?: MrtStationWire[];
  // PROP-76 — visible-review aggregate. Always present.
  review_avg: number;
  review_count: number;
}

// ─── adapted (page-level) type ───────────────────────────────────────────────

// What PropertyView consumes after adaptProjectDetails. Pricing /
// unit-mix are intentionally absent — project_details carries neither.
export interface PropertyDetail {
  name: string;
  // PROP-82 — needed by the report-inaccuracy button so the FE can
  // POST to /api/projects/{slug}/report without having to round-trip
  // back to the wire. Always present (project_details.slug is NOT
  // NULL); empty string is the safe fallback for offline mocks.
  slug: string;
  title: string;
  description: string;
  category?: string;
  tenure?: string;
  district?: string;
  marketSegment?: string;
  propertyTypes: string[];
  planningArea?: string;
  topYear?: number;
  lat?: number;
  lng?: number;
  nearbyMrt: MrtStationWire[];
  // Raw specs_parsed blob, passed through for the "What's included"
  // section. Loosely typed on purpose — the cos.sg specs schema is
  // volatile, so the consumer reads it defensively.
  specs: Record<string, unknown>;
  tone: Tone;
  // PROP-76 — same aggregate the modal header + PropertyReviews
  // section render. Adapter coerces missing fields to 0 so the
  // renderer never sees undefined.
  reviewAvg: number;
  reviewCount: number;
}

// ─── pure-visual helpers ─────────────────────────────────────────────────────

const TONES: Tone[] = ["forest", "cream", "slate", "rose", "sky"];

// `tone` is a pure-visual palette key with no server source. Derive it
// deterministically from the project name so a given project always
// renders with the same palette across cards and the detail view.
export function toneFromName(name: string): Tone {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) | 0;
  }
  return TONES[Math.abs(h) % TONES.length];
}

// titlecase normalises a URA string (which may arrive UPPERCASE or
// already TitleCase across endpoints) to display form. Lowercases then
// capitalises each word — good enough for street / project names.
function titlecase(s: string): string {
  return s
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

// Normalises a URA district to display form: "D09" / "09" / "9" → "D9".
// Leaves anything non-matching untouched.
function trimDistrict(d: string): string {
  const m = /^D?0*(\d+)$/i.exec(d.trim());
  return m ? `D${m[1]}` : d.trim();
}

// coerceYear defends against placeholder / out-of-range values in
// specs_parsed.top_year (string or number). Returns undefined when the
// value isn't a plausible TOP year.
function coerceYear(v: unknown): number | undefined {
  const n = typeof v === "number" ? v : typeof v === "string" ? parseInt(v, 10) : NaN;
  if (!Number.isFinite(n) || n < 1960 || n > 2099) return undefined;
  return n;
}

// ura_pipeline.status → the tag vocabulary the launch cards filter on.
// Unknown statuses fall through to a titlecased passthrough.
const STATUS_TO_TAG: Record<string, string> = {
  "new launch": "New launch",
  "new": "New launch",
  "previewing": "Previewing",
  "preview": "Previewing",
  "launching": "Launching",
  "launch": "Launching",
  "balloting": "Balloting",
  "ballot": "Balloting",
  "final phase": "Final phase",
  "final": "Final phase",
};

// ─── fetchers ────────────────────────────────────────────────────────────────

export async function listPipeline(): Promise<PipelineRowWire[]> {
  const resp = await apiJson<PipelineResp>("/dataengine/ura/pipeline");
  return resp.rows ?? [];
}

// listHottest returns up to `limit` projects ranked by ura_transactions
// volume over the trailing 90 days. The server-side default (15) keeps
// the response small for the home section; HotChartView calls with a
// larger limit for its full table.
export async function listHottest(limit = 15): Promise<HottestRowWire[]> {
  const resp = await apiJson<HottestResp>(`/dataengine/explore/hottest?limit=${limit}`);
  return resp.rows ?? [];
}

// listPicked returns the buyer "Picked for you" feed, scored on the
// server against the signed-in user's workspace prefs. Returns the full
// envelope (rows + reason) so the caller can branch on empty-state hints
// like "no_workspace" / "no_prefs" without losing the signal.
//
// workspaceId is optional — the server defaults to the user's first
// active workspace. Passing it explicitly lets a multi-workspace UI scope
// the picks. limit is server-clamped (default 6, max 12).
export async function listPicked(workspaceId?: string, limit?: number): Promise<PickedResp> {
  const q = new URLSearchParams();
  if (workspaceId) q.set("workspace_id", workspaceId);
  if (limit != null) q.set("limit", String(limit));
  const qs = q.toString();
  return await apiJson<PickedResp>(`/explore/picked${qs ? `?${qs}` : ""}`);
}

// ─── project-quality user feedback (PROP-82) ─────────────────────────────────
//
// One write endpoint. The FE form on the project detail modal posts
// here; backend handles dedup + rate-limit (per-user, per-hour).
// 401 on no cookie → FE shows the auth CTA.

export type ProjectReportCategory =
  | "wrong_unit_mix"
  | "wrong_top"
  | "wrong_tenure"
  | "wrong_floorplan"
  | "other";

// Display labels for the FE picker. Keep in lockstep with the
// backend constants.ProjectReportCategories (Go side): adding a
// new value here without the matching server constant will get a
// 400 from the validator.
export const PROJECT_REPORT_CATEGORIES: { value: ProjectReportCategory; label: string }[] = [
  { value: "wrong_unit_mix", label: "Unit mix is wrong" },
  { value: "wrong_top",      label: "TOP year is wrong" },
  { value: "wrong_tenure",   label: "Tenure is wrong" },
  { value: "wrong_floorplan", label: "Floor plan looks wrong / cropped" },
  { value: "other",          label: "Something else" },
];

export interface ProjectReportResp {
  id: number;
  project_slug: string;
  user_id: string;
  category: ProjectReportCategory;
  note?: string;
  created_at: string;
}

export async function submitProjectReport(
  slug: string,
  body: { category: ProjectReportCategory; note?: string },
): Promise<ProjectReportResp> {
  return apiPostJson<ProjectReportResp>(
    `/api/projects/${encodeURIComponent(slug)}/report`,
    body,
  );
}

// ─── greeting (PROP-71) ──────────────────────────────────────────────────────
//
// /explore/greeting answers a single buyer hero line:
// "Good {timeOfDay}. <i>{headline}</i>". The server walks a four-source
// priority cascade — shortlist movement (signed-in), URA flash article,
// any fresh article, recent project launch — and falls back to a
// stable static line when nothing is fresh. Auth is optional: 401 from
// a stale cookie is treated as anonymous, never as a hard fail.

// FALLBACK_HEADLINE mirrors greeting.FallbackHeadline (backend). Lives
// here so the hook can render the same string instantly while the
// network resolves — preserves "optimistic UI must equal post-refresh
// UI" from CLAUDE.md.
export const GREETING_FALLBACK_HEADLINE =
  "Singapore residential market, charted and explained.";

export type GreetingTimeOfDay = "morning" | "afternoon" | "evening";
export type GreetingKind = "shortlist" | "ura_flash" | "article" | "launch" | "fallback";

export interface GreetingResp {
  timeOfDay: GreetingTimeOfDay;
  headline: string;
  kind: GreetingKind;
  // Optional deep-link to /news/{slug} for the article kinds. v1 keeps
  // the hero ambient (non-clickable) so the field is reserved for a
  // follow-up iteration that wires up navigation.
  deepLink?: string;
}

export async function getGreeting(): Promise<GreetingResp> {
  return await apiJson<GreetingResp>(`/explore/greeting`);
}

// bucketTimeOfDayClientFallback computes a local-clock time-of-day so
// the FE can render the right "Good morning/afternoon/evening" prefix
// before the server response lands. Boundaries match
// greeting.BucketTimeOfDay (Asia/Singapore on the server):
//
//   [5, 12) morning · [12, 18) afternoon · everything else evening.
//
// Local time will drift from Asia/Singapore for non-SG users, but the
// server value swaps in within ~100ms — and a wrong-prefix flash for
// 100ms is still better than a blank hero.
export function bucketTimeOfDayClientFallback(now: Date = new Date()): GreetingTimeOfDay {
  const h = now.getHours();
  if (h >= 5 && h < 12) return "morning";
  if (h >= 12 && h < 18) return "afternoon";
  return "evening";
}

// getProjectDetails returns null on 404 — both `no_details` (project
// exists but has no listing-source slug) and `not_found`. The 404 is
// common for pipeline projects, so the caller degrades gracefully
// rather than treating it as an error.
export async function getProjectDetails(name: string): Promise<ProjectDetailsWire | null> {
  const res = await apiFetch(`/dataengine/projects/${encodeURIComponent(name)}/details`);
  if (res.status === 404) return null;
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new ApiError((body as { error?: string }).error || `HTTP ${res.status}`, res.status);
  }
  return (await res.json()) as ProjectDetailsWire;
}

// getProjectFloorplan returns null on 404 — `no_floorplan` (extraction
// hasn't been run yet, common for pipeline projects) and `not_found`
// (no projects row) both degrade to the same "no plans yet" UI.
export async function getProjectFloorplan(name: string): Promise<FloorplanProjectWire | null> {
  const res = await apiFetch(`/dataengine/projects/${encodeURIComponent(name)}/floorplan`);
  if (res.status === 404) return null;
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new ApiError((body as { error?: string }).error || `HTTP ${res.status}`, res.status);
  }
  return (await res.json()) as FloorplanProjectWire;
}

// listProjectAssets fetches the asset rows for a given kind (e.g.
// "floor_plan"). 200 with `rows: []` is the legitimate "no slug yet"
// answer from the backend — there's no 404 path here.
export async function listProjectAssets(name: string, kind: string): Promise<AssetRowWire[]> {
  const resp = await apiJson<AssetPageWire>(
    `/dataengine/projects/${encodeURIComponent(name)}/assets?kind=${encodeURIComponent(kind)}`,
  );
  return resp.rows ?? [];
}

// ─── sample-price resolver (PROP-235 / PROP-243) ─────────────────────────────

// Re-export so callers (CostsTab, the parity test) import the default and
// the resolver from one module — mirrors the iOS resolveSamplePrice
// contract, which imports COSTS_DEFAULT_SAMPLE_PRICE from lib/exploreApi.
export { COSTS_DEFAULT_SAMPLE_PRICE } from "@/constants/costs";

// Where the Costs-tab quantum came from. Travels WITH the number so the
// UI can label the basis honestly — a district median must never read as
// this project's asking price, and the default must never read as data.
export type SamplePriceSource = "dev_sales" | "txn_median" | "district_median" | "default";

export interface SamplePriceResult {
  price: number;
  source: SamplePriceSource;
  // "YYYY-MM" of the underlying signal (dev-sales ref period, latest txn
  // month, or district anchor month). Absent for the default.
  period?: string;
  // Number of transactions behind a computed median (txn_median /
  // district_median). Absent for dev_sales (a single reported figure)
  // and default.
  sampleCount?: number;
}

// Median of a numeric list (caller guarantees non-empty). Sorts a copy so
// the input order (contract_date DESC from the wire) is preserved.
function medianOf(nums: number[]): number {
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 1 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

// "YYYY-MM-DD" / "YYYY-MM…" → "YYYY-MM". Returns undefined for anything
// that doesn't start with a year-month so a bad date never fabricates a
// period label.
function monthOf(date?: string | null): string | undefined {
  if (!date) return undefined;
  const m = /^(\d{4})-(\d{2})/.exec(date);
  return m ? `${m[1]}-${m[2]}` : undefined;
}

// URA district → the 2-digit zero-padded form the district_median handler
// expects ("D9" / "d09" / "9" → "09"). Returns null when there's no digit
// to key on ("Orchard"), or when the number falls outside the 1..28
// planning-district range the backend (`normaliseDistrict`) accepts — so
// the caller skips the district query rather than firing a doomed request.
function districtToQueryParam(district?: string): string | null {
  if (!district) return null;
  const m = /(\d+)/.exec(district);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (!Number.isFinite(n) || n < 1 || n > COSTS_MAX_DISTRICT) return null;
  return String(n).padStart(2, "0");
}

// resolveSamplePrice walks a live-data priority chain to find a
// representative per-project quantum for the Costs tab. project_details
// carries no pricing, so we resolve it here — in code, never through the
// LLM. Priority: developer-sales median → recent-window transacted
// median → district median → clearly-labelled default. Every source is
// wrapped so a throw / absent signal degrades to the next; the function
// never rejects and always returns a labelled result.
export async function resolveSamplePrice(
  projectName: string,
  district?: string,
): Promise<SamplePriceResult> {
  const enc = encodeURIComponent(projectName);

  // 1) Developer-sales median — the strongest per-project signal. Rows
  //    come back ref_period DESC; take the most recent with a real
  //    (>0) median. median=0 is "reported, no median", not S$0.
  try {
    const resp = await apiJson<{ rows: DeveloperSalesRowWire[] | null }>(
      `/dataengine/ura/developer-sales?project=${enc}`,
    );
    const row = (resp.rows ?? []).find((r) => (r.median_price ?? 0) > 0);
    if (row) {
      return { price: row.median_price, source: "dev_sales", period: row.ref_period };
    }
  } catch {
    // degrade to the next source
  }

  // 2) Recent transacted median — median of this project's sales within a
  //    trailing window anchored on its latest (positive-priced) sale.
  try {
    const resp = await apiJson<{ rows: UraTxnRowWire[] | null }>(
      `/dataengine/ura/transactions?project=${enc}`,
    );
    const priced = (resp.rows ?? []).filter((r) => (r.price ?? 0) > 0 && !!r.contract_date);
    if (priced.length > 0) {
      // Anchor on the most recent sale, then trim to the trailing window.
      const anchor = priced.reduce((a, b) => (b.contract_date > a.contract_date ? b : a));
      const cutoff = new Date(anchor.contract_date);
      cutoff.setMonth(cutoff.getMonth() - COSTS_TXN_WINDOW_MONTHS);
      const cutoffISO = cutoff.toISOString().slice(0, 10);
      const inWindow = priced.filter((r) => r.contract_date >= cutoffISO);
      if (inWindow.length > 0) {
        return {
          price: medianOf(inWindow.map((r) => r.price)),
          source: "txn_median",
          period: monthOf(anchor.contract_date),
          sampleCount: inWindow.length,
        };
      }
    }
  } catch {
    // degrade to the next source
  }

  // 3) District median — a district-wide recent-window median. Only when
  //    the district normalises to a queryable code; a name-only district
  //    ("Orchard") skips this rung rather than firing a bad query.
  const districtParam = districtToQueryParam(district);
  if (districtParam) {
    try {
      const resp = await apiJson<DistrictMedianWire>(
        `/dataengine/explore/district_median?district=${districtParam}`,
      );
      if (resp.median_price != null && resp.median_price > 0) {
        return {
          price: resp.median_price,
          source: "district_median",
          period: monthOf(resp.anchor_date),
          sampleCount: resp.sample_count,
        };
      }
    } catch {
      // degrade to the default
    }
  }

  // 4) No live signal — the clearly-labelled generic default.
  return { price: COSTS_DEFAULT_SAMPLE_PRICE, source: "default" };
}

// ─── adapters ────────────────────────────────────────────────────────────────

// adaptPipelineRow maps a ura_pipeline row to the LaunchProperty shape
// the launch cards render. ura_pipeline has no pricing column, so
// priceFrom / psf are explicit placeholders — never fabricated.
export function adaptPipelineRow(w: PipelineRowWire): LaunchProperty {
  const name = titlecase(w.project);
  const district = w.street
    ? `${trimDistrict(w.district)} · ${titlecase(w.street)}`
    : trimDistrict(w.district);
  const tag = STATUS_TO_TAG[w.status?.toLowerCase().trim()] ?? titlecase(w.status || "");
  return {
    name,
    district,
    tone: toneFromName(name),
    tag: tag || "Pipeline",
    priceFrom: "Pricing TBA",
    psf: "—",
    units: w.total_units > 0 ? String(w.total_units) : "—",
    imageUrl: w.image_url || "",
  };
}

// adaptProjectDetails maps the project_details wire shape to what
// PropertyView renders. display_name is URA-canonical and preferred;
// title / name are the listing-source fallback.
export function adaptProjectDetails(w: ProjectDetailsWire): PropertyDetail {
  const name = w.display_name || w.title || w.name;
  const specs = w.specs_parsed ?? {};
  return {
    name,
    slug: w.slug || "",
    title: w.title || name,
    description: w.description || "",
    category: w.category || undefined,
    tenure: w.tenure || undefined,
    district: w.district ? trimDistrict(w.district) : undefined,
    marketSegment: w.market_segment || undefined,
    propertyTypes: w.property_types ?? [],
    planningArea: w.planning_area || undefined,
    topYear: coerceYear(specs["top_year"]),
    lat: typeof w.lat === "number" ? w.lat : undefined,
    lng: typeof w.lng === "number" ? w.lng : undefined,
    // Normalise per-station codes/lines here. The backend guarantees the
    // top-level nearby_mrt is [], but each station comes from a JSONB
    // passthrough where codes/lines can be null — the render does
    // s.lines.length / s.codes.join(), which would crash. Normalise once at
    // the boundary so every consumer is safe.
    nearbyMrt: (w.nearby_mrt ?? []).map((s) => ({
      ...s,
      codes: s.codes ?? [],
      lines: s.lines ?? [],
    })),
    specs,
    tone: toneFromName(name),
    reviewAvg: typeof w.review_avg === "number" ? w.review_avg : 0,
    reviewCount: typeof w.review_count === "number" ? w.review_count : 0,
  };
}

// Format a price in S$ millions with one decimal — matches the existing
// HOTTEST mocks' `price` strings (e.g. "S$2.35M").
function fmtPriceM(n?: number): string {
  if (n == null || !Number.isFinite(n) || n <= 0) return "—";
  return `S$${(n / 1_000_000).toFixed(2)}M`;
}

// Format $/sqft with thousand-separators — matches "S$2,180 psf".
function fmtPsf(n?: number): string {
  if (n == null || !Number.isFinite(n) || n <= 0) return "—";
  return `$${Math.round(n).toLocaleString("en-SG")} psf`;
}

// Format a PSF % change for the HotRow chip — "+4.2%" / "−0.6%". Uses
// the unicode minus per the design (HOTTEST mock used "−").
function fmtChange(n?: number): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const abs = Math.abs(n).toFixed(1);
  const sign = n >= 0 ? "+" : "−";
  return `${sign}${abs}%`;
}

// adaptPickedRow maps an /explore/picked row to PickedProperty for the
// PickedCard renderer. Price/basis travel together — when the median
// lookup found no sample, both stay undefined and the card omits the
// price segment rather than rendering "S$NaN" or "S$0".
export function adaptPickedRow(w: PickedRowWire): PickedProperty {
  const name = w.display_name || w.name;
  const district = w.district ? trimDistrict(w.district) : "";
  return {
    name,
    district,
    tone: toneFromName(name),
    matchScore: Math.max(0, Math.min(100, Math.round(w.match_score))),
    price: w.price != null ? fmtPriceM(w.price) : "",
    priceBasis: w.price_basis || "",
    why: w.why,
    imageUrl: w.image_url || "",
    reviewAvg: typeof w.review_avg === "number" ? w.review_avg : 0,
    reviewCount: typeof w.review_count === "number" ? w.review_count : 0,
  };
}

// HotSort is the hot-chart ordering chosen by the FilterChips.
export type HotSort = "txns" | "change-up" | "change-down";

// sortHotChart returns a NEW array ordered by the chosen key. The PSF
// sorts key off the RAW numeric changePct — never the `change` display
// string, whose unicode minus (U+2212) / em-dash parseFloat can't read,
// which silently left every negative / no-data row unsorted (PROP-177).
// Rows with no change data sink to the bottom in BOTH directions.
export function sortHotChart(rows: HotProperty[], sort: HotSort): HotProperty[] {
  if (sort === "txns") return rows;
  const out = [...rows];
  if (sort === "change-up") {
    // Biggest gain first; no-data → -Infinity (bottom).
    out.sort((a, b) => (b.changePct ?? -Infinity) - (a.changePct ?? -Infinity));
  } else {
    // Biggest decline first; no-data → +Infinity (bottom).
    out.sort((a, b) => (a.changePct ?? Infinity) - (b.changePct ?? Infinity));
  }
  return out;
}

// adaptHottestRow maps an /explore/hottest row to HotProperty for the
// existing HotRow / HotChartView renderers. Drops the legacy `views`
// field — txn_count replaces it server-side and the renderer is
// relabelled to match.
export function adaptHottestRow(w: HottestRowWire): HotProperty {
  const name = w.display_name || w.name;
  return {
    name,
    district: w.district ? trimDistrict(w.district) : "—",
    type: w.property_type || "",
    tone: toneFromName(name),
    price: fmtPriceM(w.median_price),
    psf: fmtPsf(w.median_psf),
    change: fmtChange(w.psf_change_pct),
    changePct:
      typeof w.psf_change_pct === "number" && Number.isFinite(w.psf_change_pct)
        ? w.psf_change_pct
        : null,
    dir: w.direction,
    txnCount: w.txn_count,
    imageUrl: w.image_url || "",
    reviewAvg: typeof w.review_avg === "number" ? w.review_avg : 0,
    reviewCount: typeof w.review_count === "number" ? w.review_count : 0,
  };
}
